/* eslint-disable */
import React, { useEffect, useMemo, useState } from 'react'
import { isEmpty } from 'lodash'
import { differenceInCalendarDays } from 'date-fns'
import drawEventMouseMove from '../../../drawing/drawEventMouseMove'
import drawEvents from '../../../drawing/drawEvents'
import drawDiagonalLines from '../../../drawing/drawDiagonalLines'
import drawGrid from '../../../drawing/drawGrid'
import drawSummary from '../../../drawing/drawSummary'
import drawSummaryIcons from '../../../drawing/drawSummaryIcons'
import { imageCacheService } from '../../../drawing/images/imageCache'
import { createCacheCanvas } from '../../../utils/Canvas'
import clamp from '../../../utils/clamp'

export default ({
  width,
  height,
  constants,
  dpConstants,
  rooms,
  startRoomIdx,
  scrollTop,
  scrollLeft,
  startDay,
  selectedDays,
  hoveredEvent,
  shape,
  colors,
  summary,
  summarySettings,
  eventLabels,
  mergeEventLabels = null,
  minDate = null,
  doLabel,
  showOriginIcon = false,
}) => {
  const [canvas, setCanvas] = useState(null)
  const [selectedDayBuffer, setSelectedDayBuffer] = useState(null)
  const [summaryIconsBuffer, setSummaryIconsBuffer] = useState(null)
  const [drawnDay, setDrawnDay] = useState(null)
  const [drawnRoomIx, setDrawnRoomIdx] = useState(null)
  const bufferCanvas = useMemo(() => document.createElement('canvas'), [])
  const joinCanvas = useMemo(() => document.createElement('canvas'), [])
  const summaryCanvas = useMemo(() => document.createElement('canvas'), [])
  const [canvasWidth, setCanvasWidth] = useState(
    width * dpConstants.PIXEL_RATIO,
  )
  useEffect(() => setCanvasWidth(width * dpConstants.PIXEL_RATIO), [
    width,
    dpConstants,
  ])
  const [canvasHeight, setCanvasHeight] = useState(
    width * dpConstants.PIXEL_RATIO,
  )
  useEffect(() => setCanvasHeight(height * dpConstants.PIXEL_RATIO), [
    height,
    dpConstants,
  ])

  const roomsShowCount = useMemo(
    () => Math.ceil(canvasHeight / dpConstants.GRID_HEIGHT) + 1,
    [canvasHeight, dpConstants],
  )

  const daysShownCount = useMemo(
    () => Math.ceil(canvasWidth / dpConstants.GRID_WIDTH) + 1,
    [canvasWidth, dpConstants],
  )

  useEffect(() => {
    setSelectedDayBuffer(
      createCacheCanvas(
        ctx =>
          drawDiagonalLines(
            ctx,
            dpConstants,
            0,
            0,
            dpConstants.GRID_WIDTH,
            canvasHeight + dpConstants.GRID_HEIGHT,
          ),
        dpConstants.GRID_WIDTH,
        canvasHeight,
      ),
    )
  }, [dpConstants, canvas, canvasHeight])

  useEffect(() => {
    if (summarySettings) {
      setSummaryIconsBuffer(
        createCacheCanvas(
          ctx => drawSummaryIcons(ctx, dpConstants, summarySettings),
          dpConstants.GRID_WIDTH,
          dpConstants.SUMMARY_HEIGHT,
        ),
      )
    }
  }, [dpConstants, summarySettings])

  useEffect(() => {
    summaryCanvas.height = dpConstants.SUMMARY_HEIGHT
    summaryCanvas.width = canvasWidth + dpConstants.GRID_WIDTH
    if (canvas) {
      canvas.height = canvasHeight
      canvas.width = canvasWidth
    }
  }, [canvasWidth, canvasHeight, dpConstants])

  useEffect(() => {
    bufferCanvas.height = roomsShowCount * dpConstants.GRID_HEIGHT
    joinCanvas.height = roomsShowCount * dpConstants.GRID_HEIGHT
  }, [roomsShowCount])

  useEffect(() => {
    bufferCanvas.width = daysShownCount * dpConstants.GRID_WIDTH
    joinCanvas.width = daysShownCount * dpConstants.GRID_WIDTH
  }, [daysShownCount, dpConstants])

  useEffect(() => {
    const roomDiff = clamp(
      startRoomIdx - drawnRoomIx,
      -roomsShowCount,
      +roomsShowCount,
    )

    const daysDiff = clamp(
      differenceInCalendarDays(startDay, drawnDay),
      -daysShownCount,
      +daysShownCount,
    )

    const ctx = bufferCanvas.getContext('2d')
    const joinCtx = joinCanvas.getContext('2d')
    joinCtx.clearRect(0, 0, joinCanvas.width, joinCanvas.height)
    const createdLabels = drawEvents(
      joinCtx,
      dpConstants,
      joinCanvas.height,
      joinCanvas.width,
      rooms,
      startDay,
      startRoomIdx,
      shape,
      colors,
      eventLabels,
      daysDiff > 0 ? daysShownCount - daysDiff : 0,
      daysDiff < 0 ? Math.abs(daysDiff) : daysShownCount,
      roomDiff > 0 ? roomsShowCount - roomDiff : 0,
      roomDiff < 0 ? Math.abs(roomDiff) : roomsShowCount,
      doLabel,
      false,
      showOriginIcon,
    )
    if (!isEmpty(createdLabels) && mergeEventLabels) {
      mergeEventLabels(createdLabels)
    }
    if (
      Math.abs(daysDiff) < daysShownCount &&
      Math.abs(roomDiff) < roomsShowCount
    ) {
      joinCtx.drawImage(
        bufferCanvas,
        daysDiff > 0 ? daysDiff * dpConstants.GRID_WIDTH : 0,
        roomDiff > 0 ? roomDiff * dpConstants.GRID_HEIGHT : 0,
        joinCanvas.width - Math.abs(daysDiff) * dpConstants.GRID_WIDTH,
        joinCanvas.height - Math.abs(roomDiff) * dpConstants.GRID_HEIGHT,
        daysDiff < 0 ? Math.abs(daysDiff) * dpConstants.GRID_WIDTH : 0,
        roomDiff < 0 ? Math.abs(roomDiff) * dpConstants.GRID_HEIGHT : 0,
        joinCanvas.width - Math.abs(daysDiff) * dpConstants.GRID_WIDTH,
        joinCanvas.height - Math.abs(roomDiff) * dpConstants.GRID_HEIGHT,
      )
    }
    ctx.clearRect(0, 0, bufferCanvas.width, bufferCanvas.height)
    ctx.drawImage(joinCanvas, 0, 0)

    setDrawnDay(startDay)
    setDrawnRoomIdx(startRoomIdx)
  }, [
    startRoomIdx,
    +startDay,
    showOriginIcon,
    doLabel,
    shape,
    colors,
    eventLabels,
    rooms,
    dpConstants,
    roomsShowCount,
    daysShownCount,
  ])

  useEffect(() => {
    const ctx = bufferCanvas.getContext('2d')
    ctx.clearRect(0, 0, bufferCanvas.width, bufferCanvas.height)

    if (rooms) {
      const createdLabels = drawEvents(
        ctx,
        dpConstants,
        bufferCanvas.height,
        bufferCanvas.width,
        rooms,
        startDay,
        startRoomIdx,
        shape,
        colors,
        eventLabels,
        0,
        daysShownCount,
        0,
        roomsShowCount,
        doLabel,
        false,
        showOriginIcon,
      )

      if (!isEmpty(createdLabels) && mergeEventLabels) {
        mergeEventLabels(createdLabels)
      }
    }
  }, [
    canvasHeight,
    canvasWidth,
    rooms,
    dpConstants,
    showOriginIcon,
    doLabel,
    shape,
    colors,
    eventLabels,
    startDay,
    startRoomIdx,
    roomsShowCount,
  ])

  const doSummaryDrawing = () => {
    const ctx = summaryCanvas.getContext('2d')
    ctx.clearRect(0, 0, summaryCanvas.width, summaryCanvas.height)
    if (summary) {
      drawSummary(
        ctx,
        dpConstants,
        summaryCanvas.width,
        summary,
        startDay,
        summarySettings,
        summaryIconsBuffer,
      )
    }
  }

  useEffect(() => {
    doSummaryDrawing()
  }, [
    canvasWidth,
    canvasHeight,
    summary,
    +startDay,
    dpConstants,
    summarySettings,
  ])

  const doTheDrawing = () => {
    if (canvas) {
      const yOffset =
        Math.round(scrollTop % constants.GRID_HEIGHT) * dpConstants.PIXEL_RATIO
      const xOffset =
        Math.round(scrollLeft % constants.GRID_WIDTH) * dpConstants.PIXEL_RATIO
      const ctx = canvas.getContext('2d')
      const cWidth = canvas.width
      const cHeight = canvas.height
      ctx.clearRect(0, 0, cWidth, cHeight)
      drawGrid(
        ctx,
        dpConstants,
        cHeight,
        cWidth,
        yOffset,
        xOffset,
        startDay,
        startRoomIdx,
        rooms,
        selectedDays,
        selectedDayBuffer,
      )
      drawEventMouseMove(
        ctx,
        dpConstants,
        cHeight,
        cWidth,
        yOffset,
        xOffset,
        hoveredEvent,
        shape,
      )
      ctx.drawImage(
        bufferCanvas,
        xOffset,
        yOffset,
        cWidth,
        cHeight,
        0,
        0,
        cWidth,
        cHeight,
      )
      if (summary) {
        ctx.drawImage(
          summaryCanvas,
          xOffset,
          0,
          cWidth,
          dpConstants.SUMMARY_HEIGHT,
          0,
          cHeight - dpConstants.SUMMARY_HEIGHT,
          cWidth,
          dpConstants.SUMMARY_HEIGHT,
        )
      }
    }
  }

  useEffect(() => {
    imageCacheService.addObserver(() => {
      doSummaryDrawing()
      doTheDrawing()
    })
  }, [summaryIconsBuffer, doSummaryDrawing])

  useEffect(() => {
    doTheDrawing()
  }, [
    canvasWidth,
    canvasHeight,
    rooms,
    constants,
    scrollTop,
    scrollLeft,
    hoveredEvent,
    selectedDays,
    selectedDayBuffer,
    summary,
    summarySettings,
    +minDate,
    showOriginIcon,
    doLabel,
    shape,
    colors,
    eventLabels,
    bufferCanvas,
  ])

  return (
    <canvas
      className={'CalendarCanvas'}
      ref={setCanvas}
      width={canvasWidth}
      height={canvasHeight}
      style={{
        background: '#fff',
        height: `${height}px`,
        position: 'absolute',
        width: `${width}px`,
      }}
    />
  )
}
